import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';

const WhoWeAre: React.FC = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About ERHR Global - Boston HR Consulting Firm",
    "description": "Learn about ERHR Global, Boston's trusted HR consulting partner specializing in transitional and strategic HR services for businesses in Massachusetts.",
    "url": "https://erhrglobal.com/who-we-are",
    "mainEntity": {
      "@type": "ProfessionalService",
      "name": "ERHR Global",
      "description": "Boston-based HR consulting firm providing transitional HR services and strategic HR consulting across the full employee lifecycle.",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Boston",
        "addressRegion": "MA",
        "addressCountry": "US"
      },
      "areaServed": [{"@type": "City", "name": "Boston"}, {"@type": "State", "name": "Massachusetts"}]
    }
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="About ERHR Global - HR Consulting Firm in Boston, Massachusetts"
        description="About ERHR Global — transitional & strategic HR consulting. Expert support for businesses navigating growth, transitions, and HR challenges."
        url="https://erhrglobal.com/who-we-are"
        structuredData={structuredData}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">Who We Are</h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto">
              Your trusted partner in bridging HR gaps and elevating organizational excellence through strategic human resources consulting.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="relative mb-8">
                <img 
                  src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=75" 
                  srcSet="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=75 400w, https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=75 600w, https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=75 800w"
                  sizes="(max-width: 768px) 100vw, 50vw"
                  alt="Professional HR team" 
                  className="w-full h-64 object-cover rounded-2xl"
                  width={600}
                  height={256}
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-primary-600 bg-opacity-20 rounded-2xl"></div>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>
              <p className="text-lg text-gray-600 mb-6">
                At ERHR, we believe that exceptional human resources management is the cornerstone of organizational success. Our mission is to bridge the gap between where your HR function is today and where it needs to be tomorrow.
              </p>
              <p className="text-lg text-gray-600">
                We partner with organizations to design, implement, and optimize HR strategies that drive employee engagement, operational efficiency, and business growth. Whether you're navigating a transition or planning for the future, we're here to elevate your HR capabilities.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-8"
            >
              {/* <h3 className="text-2xl font-semibold text-primary-800 mb-4">Our Vision</h3> */}
              <p className="text-primary-700 text-xl">
              <b>Transitional HR </b>team with a hands-on approach that assumes full responsibility during periods of HR instability—bridging gaps caused by turnover, limited capacity, or unfinished tasks—while also delivering</p>
              <p className="text-primary-700 text-xl mt-10">
              <b>Strategic HR consulting </b>across the full employee lifecycle, talent management, compliance, and organizational development.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why ERHR */}
      <section className="pt-10 pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <div className="bg-gray-50 rounded-2xl p-8 shadow-sm border border-gray-100">
              <p className="text-sm font-semibold text-primary-600 tracking-wide uppercase mb-2">
                Our Story
              </p>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Why ERHR?</h2>
              <p className="text-lg text-gray-600 mb-4">
                ERHR was born from a simple reality: HR needs don&apos;t always wait.
              </p>
              <p className="text-lg text-gray-600 mb-4">
                Throughout our founder&apos;s corporate HR career, particularly working with fast-growing and startup organizations, she saw the same challenge repeatedly. Businesses can grow and change faster than their internal HR infrastructure can keep up. A key team member goes on leave. Headcount increases quickly. An acquisition is approaching. Compliance requirements change. Processes that worked for 20 employees no longer work for 100.
              </p>
              <p className="text-lg text-gray-600 mb-4">
                That is where the name
                <span className="font-semibold text-primary-700"> ERHR — Emergency Response Human Resources </span>
                comes from.
              </p>
              <p className="text-lg text-gray-600 mb-4">
                We built ERHR to provide organizations with experienced HR support when they need it most, without sacrificing quality for speed. Our team can step in quickly, take ownership, and provide practical solutions while keeping the business moving.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Whether you are temporarily short-handed, experiencing rapid growth, preparing for an acquisition, covering a leave of absence, strengthening compliance, building policies and processes, or automating an HR function that has become too manual, ERHR provides the expertise and capacity to address the immediate need while building solutions that work for the future.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/transitional-hr" className="text-primary-600 font-medium hover:text-primary-700 transition-colors underline underline-offset-2">
                  Transitional HR
                </Link>
                <span className="text-gray-300">|</span>
                <Link to="/strategic-hr/employee-engagement" className="text-primary-600 font-medium hover:text-primary-700 transition-colors underline underline-offset-2">
                  Employee Engagement
                </Link>
                <span className="text-gray-300">|</span>
                <Link to="/strategic-hr/policies-procedures" className="text-primary-600 font-medium hover:text-primary-700 transition-colors underline underline-offset-2">
                  Policies & Procedures
                </Link>
                <span className="text-gray-300">|</span>
                <Link to="/pricing" className="text-primary-600 font-medium hover:text-primary-700 transition-colors underline underline-offset-2">
                  Services & Pricing
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Approach</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We combine strategic thinking with practical implementation to deliver HR solutions that work for your unique organization.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Strategic Thinking</h3>
              <p className="text-gray-600">
                We start by understanding your business goals, challenges, and culture to develop HR strategies that align with your organizational objectives.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Customized Solutions</h3>
              <p className="text-gray-600">
                Every organization is unique. We tailor our services to fit your specific needs, industry requirements, and company culture.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Results-Driven</h3>
              <p className="text-gray-600">
                We focus on delivering measurable outcomes that drive real business value and create lasting positive change in your organization.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These core values guide everything we do and shape how we partner with our clients.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "Integrity",
                description: "We operate with honesty, transparency, and ethical standards in all our interactions.",
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                )
              },
              {
                title: "Excellence",
                description: "We strive for the highest quality in our services and continuously improve our expertise.",
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                )
              },
              {
                title: "Partnership",
                description: "We work collaboratively with our clients as true partners in their success.",
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                )
              },
              {
                title: "Innovation",
                description: "We embrace new ideas and technologies to deliver cutting-edge HR solutions.",
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                )
              }
            ].map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="text-primary-600">
                    {value.icon}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-4">Ready to Partner With Us?</h2>
            <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
              Let's start a conversation about how we can help bridge the gap and elevate your HR function.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-primary-700 px-8 py-4 rounded-lg font-semibold hover:bg-primary-50 transition-colors duration-300"
              >
                Get In Touch
              </Link>
              <Link
                to="/schedule-appointment"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-primary-700 transition-colors duration-300"
              >
                Schedule Consultation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default WhoWeAre;
